// Cuánto se cobra en caja con el reparto del servidor, sobre el neto de cada línea: contado = Σ neto;
// crédito = enganche + Σ(neto − financedCents), o la regla vieja si no hay reparto. Puro, sin React.
import { cartLinesOf, type CartLine } from './cartLines';
import { isReferredWithExtras } from './externalReferral';
import { linePhoneCount } from './productSuggestions';
import type { ReservedLine, WizardSession } from './types';

/** Fila del cobro o del resumen: una línea apartada con su parte financiada y su parte de contado. */
export interface ChargeLine extends CartLine {
    financed: boolean;
    financedCents: number;
    /** `null` mientras la línea no tiene precio. */
    cashCents: number | null;
}

function liveLines(session: WizardSession): ReservedLine[] {
    return (session.reservedLines ?? []).filter((l) => l.status !== 'RELEASING');
}

/** Líneas vivas que SureKeep puede financiar. */
export function financeableLinesOf(session: WizardSession): ReservedLine[] {
    return liveLines(session).filter((l) => l.financeable);
}

/** Las financiables en el vocabulario del carrito. */
export function financeableCartLinesOf(
    session: WizardSession,
    serviceSkus: ReadonlySet<string> = new Set(),
    descriptions: ReadonlyMap<string, string> = new Map(),
): CartLine[] {
    const ids = new Set(financeableLinesOf(session).map((l) => l.lineId));
    return cartLinesOf(session, serviceSkus, descriptions).filter(
        (l) => l.lineId !== null && ids.has(l.lineId),
    );
}

/** Línea financiada según la regla vieja: la que fijó el servidor o la única financiable. */
export function financingLineOf(session: WizardSession): ReservedLine | undefined {
    const financeable = financeableLinesOf(session);
    return (
        financeable.find((l) => l.lineId === session.financedLineId) ??
        (financeable.length === 1 ? financeable[0] : undefined)
    );
}

/**
 * Parte financiada por línea que manda el servidor: la congelada al cotizar o, antes, el reparto
 * provisional. `undefined` si no hay ninguna de las dos (se aplica la regla vieja).
 */
export function serverSplitOf(session: WizardSession): Map<string, number> | undefined {
    const reserved = (session.reservedLines ?? []).filter((l) => l.status === 'RESERVED');
    if (reserved.some((l) => l.financedCents !== undefined)) {
        return new Map(reserved.map((l) => [l.lineId, l.financedCents ?? 0]));
    }
    const preview = session.financingPreview;
    if (preview === undefined) return undefined;
    return new Map(preview.lines.map((l) => [l.lineId, l.financedCents]));
}

/** Regla vieja: la financiada va entera a crédito, junto con las marcadas CREDIT al cotizar. */
function legacyFinancedIds(session: WizardSession): Set<string | null> {
    const ids = new Set<string | null>([null]);
    const financedId = financingLineOf(session)?.lineId;
    if (financedId !== undefined) ids.add(financedId);
    for (const l of session.reservedLines ?? []) {
        if (l.paymentType === 'CREDIT') ids.add(l.lineId);
    }
    return ids;
}

/** Neto de la línea como lo calcula el servidor: max(0, base × qty − discountCents). */
function withNet(line: CartLine, discounts: ReadonlyMap<string, number>): CartLine {
    const discount = line.lineId === null ? undefined : discounts.get(line.lineId);
    if (discount === undefined || line.totalCents === null) return line;
    return { ...line, totalCents: Math.max(0, line.totalCents - discount) };
}

function discountsOf(session: WizardSession): Map<string, number> {
    return new Map(
        (session.reservedLines ?? []).flatMap((l) =>
            l.discountCents !== undefined ? [[l.lineId, l.discountCents] as const] : [],
        ),
    );
}

function withSplit(
    line: CartLine,
    financedCents: number,
    financed = financedCents > 0,
): ChargeLine {
    const cashCents =
        line.totalCents === null ? null : Math.max(0, line.totalCents - financedCents);
    return { ...line, financed, financedCents, cashCents };
}

/**
 * Líneas que se cobran. En crédito cada una lleva lo financiado según el servidor; la sesión sin
 * líneas aporta su equipo. `serviceSkus` y `descriptions` etiquetan servicios y nombran kits.
 */
export function chargeLinesOf(
    session: WizardSession,
    {
        credit,
        serviceSkus,
        descriptions,
    }: {
        credit: boolean;
        serviceSkus?: ReadonlySet<string>;
        descriptions?: ReadonlyMap<string, string>;
    },
): ChargeLine[] {
    const discounts = discountsOf(session);
    const rows = cartLinesOf(session, serviceSkus, descriptions)
        .filter((l) => l.status === 'RESERVED')
        .map((l) => withNet(l, discounts));
    if (!credit) return rows.map((l) => withSplit(l, 0));
    const split = serverSplitOf(session);
    if (split !== undefined) {
        return rows.map((l) => withSplit(l, l.lineId === null ? 0 : (split.get(l.lineId) ?? 0)));
    }
    const legacy = legacyFinancedIds(session);
    return rows.map((l) =>
        legacy.has(l.lineId) ? withSplit(l, l.totalCents ?? 0, true) : withSplit(l, 0),
    );
}

export function linesTotalCents(lines: CartLine[]): number {
    return lines.reduce((sum, l) => sum + (l.totalCents ?? 0), 0);
}

/** Cobro de contado: la suma de las líneas apartadas. */
export function cashChargeCents(session: WizardSession): number {
    return linesTotalCents(chargeLinesOf(session, { credit: false }));
}

/** Cobro a crédito: el enganche más la parte de contado de cada línea. */
export function downPaymentChargeCents(session: WizardSession, downPaymentCents: number): number {
    const lines = chargeLinesOf(session, { credit: true });
    return downPaymentCents + lines.reduce((sum, l) => sum + (l.cashCents ?? 0), 0);
}

/** El teléfono y los regalos que suelta la derivación: su liberación no frena el cobro de extras. */
export function leftByReferral(line: ReservedLine): boolean {
    return line.status === 'RELEASING' && (line.role === 'GIFT' || linePhoneCount(line) > 0);
}

/** Líneas que deciden «Continuar»; en la venta derivada no cuenta lo que se está soltando. */
export function continueGateLinesOf(session: WizardSession): CartLine[] {
    const lines = cartLinesOf(session);
    if (!isReferredWithExtras(session)) return lines;
    const left = new Set((session.reservedLines ?? []).filter(leftByReferral).map((l) => l.lineId));
    return lines.filter((l) => l.lineId === null || !left.has(l.lineId));
}

/** Líneas a medio apartar o a medio liberar: con alguna, el silo rechaza el cobro. */
export function unchargeableLinesOf(session: WizardSession): ReservedLine[] {
    const cashOnly = isReferredWithExtras(session);
    return (session.reservedLines ?? []).filter(
        (l) => l.status !== 'RESERVED' && !(cashOnly && leftByReferral(l)),
    );
}
